import React, { useEffect, useMemo, useState } from "react";
import {
  ArrowForward,
  AutoAwesome,
  Bolt,
  Business,
  CheckCircle,
  Description,
  FactCheckOutlined,
  Groups,
  Insights,
  LockOutlined,
  Payments,
  PersonAddAlt,
  Schedule,
  Send,
  SupportAgent,
  TaskAlt,
  TimelineOutlined,
  TrendingUp,
  ViewKanbanOutlined,
  ViewListOutlined,
  WarningAmber,
} from "@mui/icons-material";
import { getWorkspaceClientLogos, submitWorkspaceEnquiry } from "../../../services/workspaceApi";

const dashboardModes = [
  {
    key: "Delivery",
    icon: TaskAlt,
    eyebrow: "Delivery control",
    headline: "Every commitment, owner and risk in one operating view.",
    metrics: [
      ["Active projects", "18", "+3 this month"],
      ["On-time delivery", "94%", "+6.4%"],
      ["Open work", "126", "23 due this week"],
      ["At risk", "7", "Needs attention"],
    ],
    focus: "Website transformation",
    progress: 72,
    rows: [
      ["Checkout QA", "Nishant", "Today", "In review"],
      ["Client approval", "Aarav", "Tomorrow", "Waiting"],
      ["Production release", "Shubhangi", "04 Aug", "Planned"],
    ],
  },
  {
    key: "CRM",
    icon: Business,
    eyebrow: "Revenue workspace",
    headline: "Move every relationship from first conversation to delivery.",
    metrics: [
      ["Live pipeline", "₹18.4L", "+12.8%"],
      ["Qualified leads", "42", "11 priority"],
      ["Proposals", "16", "₹7.2L value"],
      ["Follow-ups", "9", "Due today"],
    ],
    focus: "Enterprise automation",
    progress: 64,
    rows: [
      ["Discovery completed", "Product team", "Today", "Qualified"],
      ["Solution proposal", "Sales desk", "Tomorrow", "Draft"],
      ["Decision follow-up", "Nishant", "05 Aug", "Scheduled"],
    ],
  },
  {
    key: "People",
    icon: Groups,
    eyebrow: "People operations",
    headline: "Connect capacity, attendance and accountability to real work.",
    metrics: [
      ["Team members", "38", "34 active"],
      ["Available today", "29", "76% capacity"],
      ["Leave requests", "4", "2 awaiting"],
      ["Utilization", "81%", "+4.1%"],
    ],
    focus: "Delivery team capacity",
    progress: 81,
    rows: [
      ["Engineering", "12 people", "84%", "Healthy"],
      ["Training", "9 people", "78%", "Balanced"],
      ["Client success", "6 people", "91%", "High load"],
    ],
  },
  {
    key: "Finance",
    icon: Payments,
    eyebrow: "Financial clarity",
    headline: "See earnings, collections and delivery economics together.",
    metrics: [
      ["Recognized revenue", "₹12.8L", "+9.6%"],
      ["Receivables", "₹3.1L", "6 invoices"],
      ["Operating margin", "41%", "+3.2%"],
      ["Payouts due", "₹1.4L", "Next 7 days"],
    ],
    focus: "Monthly collection target",
    progress: 86,
    rows: [
      ["Lyriclious Academy", "INV-1048", "₹1.2L", "Paid"],
      ["Orbit Systems", "INV-1051", "₹84K", "Due"],
      ["Northstar Labs", "INV-1054", "₹1.6L", "Sent"],
    ],
  },
];

const capabilities = [
  {
    icon: TaskAlt,
    title: "Projects and work",
    text: "Plan projects, sprints, tasks, bugs, tickets and approvals with clear ownership.",
  },
  {
    icon: Business,
    title: "Clients and CRM",
    text: "Keep leads, proposals, clients, delivery context and follow-ups connected.",
  },
  {
    icon: Groups,
    title: "People operations",
    text: "Manage teams, attendance, leave, roles, capacity and performance signals.",
  },
  {
    icon: Payments,
    title: "Finance control",
    text: "Track invoices, payments, payouts, refunds, wallets and operational margin.",
  },
  {
    icon: Description,
    title: "Knowledge and docs",
    text: "Turn SOPs, client files, project notes and training material into shared context.",
  },
  {
    icon: AutoAwesome,
    title: "Applied intelligence",
    text: "Generate work, surface risk and summarize activity inside the operating flow.",
  },
];

const workflowSteps = [
  ["01", "Capture", "Bring client needs, requests and ideas into one accountable system."],
  ["02", "Coordinate", "Connect people, priorities, dates, dependencies and decisions."],
  ["03", "Deliver", "Move work through execution with live ownership and risk visibility."],
  ["04", "Learn", "Use operational history to improve planning, margin and performance."],
];

const repeatUntil = (items, minimumCount) => {
  if (!items.length) return [];
  const repeatedItems = [...items];
  while (repeatedItems.length < minimumCount) repeatedItems.push(...items);
  return repeatedItems.slice(0, Math.max(minimumCount, items.length));
};

const initialEnquiry = {
  name: "",
  phone: "",
  company: "",
  website: "",
};

export function WorkspaceLanding({ onboardingPrompt = false, onOnboardingChoice, onOpenDocumentation, signInUrl, signUpUrl }) {
  const [activeMode, setActiveMode] = useState("Delivery");
  const [activeProjectView, setActiveProjectView] = useState("Board");
  const [clientLogoState, setClientLogoState] = useState({
    loading: true,
    companies: [],
  });
  const [enquiry, setEnquiry] = useState(initialEnquiry);
  const [enquiryState, setEnquiryState] = useState({
    submitting: false,
    error: "",
    success: "",
  });
  const currentMode =
    dashboardModes.find((mode) => mode.key === activeMode) || dashboardModes[0];
  useEffect(() => {
    let mounted = true;

    const loadClientCompanies = async () => {
      try {
        const data = await getWorkspaceClientLogos();
        const companyMap = new Map();

        (data.testimonials || []).forEach((item) => {
          const name = `${item.company_name || ""}`.trim();
          const logo = item.company_logo?.url || "";
          if (!name || !logo) return;

          const key = name.toLowerCase();
          if (!companyMap.has(key)) companyMap.set(key, { name, logo });
        });

        if (mounted) {
          setClientLogoState({
            loading: false,
            companies: Array.from(companyMap.values()),
          });
        }
      } catch (error) {
        if (mounted) setClientLogoState({ loading: false, companies: [] });
      }
    };

    loadClientCompanies();

    return () => {
      mounted = false;
    };
  }, []);
  useEffect(() => {
    if (!onboardingPrompt) return undefined;
    const frame = window.requestAnimationFrame(() => {
      document.getElementById("workspace-onboarding")?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [onboardingPrompt]);
  const clientLogoMarquee = useMemo(() => {
    const base = repeatUntil(clientLogoState.companies, 10);
    return [...base, ...base];
  }, [clientLogoState.companies]);
  const updateEnquiry = (event) => {
    const { name, value } = event.target;
    setEnquiry((current) => ({ ...current, [name]: value }));
    if (enquiryState.error) {
      setEnquiryState((current) => ({ ...current, error: "" }));
    }
  };
  const handleEnquirySubmit = async (event) => {
    event.preventDefault();
    setEnquiryState({ submitting: true, error: "", success: "" });

    try {
      const response = await submitWorkspaceEnquiry({
        name: enquiry.name,
        phone: enquiry.phone,
        company: enquiry.company,
        website: enquiry.website,
        pagePath: typeof window !== "undefined" ? window.location.href : "/",
      });
      setEnquiry(initialEnquiry);
      setEnquiryState({
        submitting: false,
        error: "",
        success:
          response?.message ||
          "Thank you. Our Workspace team will contact you shortly.",
      });
    } catch (error) {
      setEnquiryState({
        submitting: false,
        success: "",
        error:
          error?.response?.data?.message ||
          "We could not submit your enquiry. Please try again.",
      });
    }
  };
  const chooseOnboarding = (onboardingPreference) => {
    setEnquiry((current) => ({ ...current, onboardingPreference }));
    onOnboardingChoice?.(onboardingPreference);
    window.requestAnimationFrame(() => {
      document
        .getElementById("workspace-enquiry")
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  };

  return (
    <main className="workspace-landing">
      <section className="workspace-landing-hero">
        <div className="workspace-orbit-field" aria-hidden="true">
          <span className="workspace-orbit-ring ring-one" />
          <span className="workspace-orbit-ring ring-two" />
          <i className="workspace-orbit-node node-one" />
          <i className="workspace-orbit-node node-two" />
          <i className="workspace-orbit-node node-three" />
        </div>
        <div className="workspace-landing-hero-copy">
          <span className="workspace-landing-kicker">
            <Bolt fontSize="small" />
            Company operations, connected
          </span>
          <h1>
            Run your company from
            <span> one intelligent workspace.</span>
          </h1>
          <p>
            Connect delivery, CRM, people and finance with clear ownership in one place.
          </p>
          <div className="workspace-launch-offer">
            <div className="workspace-launch-offer-icon">
              <PersonAddAlt fontSize="small" />
            </div>
            <div>
              <span>Launch offer</span>
              <strong>First 2 projects and 10 employees free for lifetime.</strong>
              <small>Then INR 49 per active employee/month from the 11th employee.</small>
            </div>
          </div>
          <div className="workspace-landing-actions">
            <a className="workspace-landing-primary" href={signUpUrl}>
              Create your free account
              <ArrowForward fontSize="small" />
            </a>
            <a
              className="workspace-landing-secondary"
              href="#workspace-enquiry"
            >
              <SupportAgent fontSize="small" />
              Request a walkthrough
            </a>
          </div>
        </div>

        <div className="workspace-product-stage" aria-label="Interactive Workspace dashboard preview">
          <div className="workspace-product-glow" />
          <div className="workspace-product-floating-signal signal-delivery">
            <span>Delivery health</span>
            <strong>94%</strong>
            <small>On-time performance</small>
          </div>
          <div className="workspace-product-floating-signal signal-live">
            <i />
            <span>Live operations</span>
          </div>
          <div className="workspace-product-floating-signal signal-control">
            <span>Control layer</span>
            <strong>One source</strong>
            <small>Every team connected</small>
          </div>
          <div className="workspace-product-window">
            <div className="workspace-product-window-bar">
              <div className="workspace-product-window-brand">
                <img alt="" src="/icons/lyriclious-icon.png" />
                <div>
                  <strong>Lyriclious Workspace</strong>
                  <small>Operations command center</small>
                </div>
              </div>
              <div className="workspace-product-window-actions">
                <Schedule fontSize="small" />
                <span className="workspace-product-avatar">NS</span>
              </div>
            </div>

            <div className="workspace-product-layout">
              <nav className="workspace-product-mini-nav" aria-label="Dashboard preview modes">
                {dashboardModes.map(({ key, icon: Icon }) => (
                  <button
                    aria-label={key}
                    className={activeMode === key ? "active" : ""}
                    key={key}
                    onClick={() => setActiveMode(key)}
                    title={key}
                    type="button"
                  >
                    <Icon fontSize="small" />
                  </button>
                ))}
              </nav>

              <div className="workspace-product-dashboard">
                <div className="workspace-product-dashboard-heading">
                  <div>
                    <span>{currentMode.eyebrow}</span>
                    <h2>{currentMode.key} overview</h2>
                  </div>
                  <small>Live workspace</small>
                </div>

                <div className="workspace-product-metrics">
                  {currentMode.metrics.map(([label, value, change]) => (
                    <article key={label}>
                      <span>{label}</span>
                      <strong>{value}</strong>
                      <small>{change}</small>
                    </article>
                  ))}
                </div>

                <div className="workspace-product-detail-grid">
                  <section className="workspace-product-focus">
                    <div>
                      <span>Current focus</span>
                      <strong>{currentMode.focus}</strong>
                    </div>
                    <div className="workspace-product-progress-label">
                      <span>Progress</span>
                      <strong>{currentMode.progress}%</strong>
                    </div>
                    <div className="workspace-product-progress">
                      <i style={{ width: `${currentMode.progress}%` }} />
                    </div>
                    <div className="workspace-product-signal-row">
                      <span><TrendingUp fontSize="small" />Momentum healthy</span>
                      <span><WarningAmber fontSize="small" />2 decisions due</span>
                    </div>
                  </section>

                  <section className="workspace-product-insight">
                    <span><Insights fontSize="small" />Operational signal</span>
                    <strong>Teams with confirmed owners close work 31% faster.</strong>
                    <p>Workspace keeps ownership visible from request to outcome.</p>
                  </section>
                </div>

                <div className="workspace-product-table">
                  <div className="workspace-product-table-heading">
                    <span>Priority</span>
                    <span>Owner</span>
                    <span>Due</span>
                    <span>Status</span>
                  </div>
                  {currentMode.rows.map((row) => (
                    <div className="workspace-product-table-row" key={row.join("-")}>
                      {row.map((value, index) => (
                        <span className={index === 3 ? "status" : ""} key={`${value}-${index}`}>
                          {value}
                        </span>
                      ))}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="workspace-product-mode-switcher">
            {dashboardModes.map(({ key, icon: Icon }) => (
              <button
                className={activeMode === key ? "active" : ""}
                key={key}
                onClick={() => setActiveMode(key)}
                type="button"
              >
                <Icon fontSize="small" />
                {key}
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="workspace-client-showcase" aria-labelledby="workspace-client-showcase-title">
        <div className="workspace-client-showcase-copy">
          <span>Client companies</span>
          <h2 id="workspace-client-showcase-title">Companies that have worked with Lyriclious.</h2>
          <p>
            Teams across services, hiring and operations work with Lyriclious to move faster with clearer ownership.
          </p>
        </div>

        <div className="workspace-client-showcase-panel">
          <div className="workspace-client-showcase-header">
            <strong>{clientLogoState.companies.length || "Live"} client network</strong>
            <span>Hiring, services, workspace and growth programs</span>
          </div>
          {clientLogoState.loading ? (
            <div className="workspace-client-logo-skeleton" aria-label="Loading client companies">
              {[1, 2, 3, 4, 5, 6].map((item) => <i key={item} />)}
            </div>
          ) : clientLogoState.companies.length ? (
            <div className="workspace-client-logo-marquee" aria-label="Lyriclious client companies">
              <div>
                {clientLogoMarquee.map((company, index) => (
                  <article key={`${company.name}-${index}`} title={company.name}>
                    <img alt={company.name} draggable="false" src={company.logo} />
                  </article>
                ))}
              </div>
            </div>
          ) : (
            <div className="workspace-client-empty">
              <Business fontSize="small" />
              <span>Approved client logos will appear here automatically.</span>
            </div>
          )}
          <div className="workspace-client-showcase-stats">
            <span><strong>Delivery teams</strong> Projects, requests and approvals handled with one clear system</span>
            <span><strong>Client collaboration</strong> Communication, files and decisions kept close to the work</span>
            <span><strong>Leadership visibility</strong> Workload, ownership and progress visible without chasing updates</span>
          </div>
        </div>
      </section>

      <section className="workspace-onboarding-section" id="workspace-onboarding">
        <div className="workspace-landing-section-heading">
          <span>Start Workspace setup</span>
          <h2>Share the basics and we will help you begin.</h2>
          <p>
            Send your name, mobile number and company name. Our team will verify the
            request and guide the next step.
          </p>
        </div>

        <div className="workspace-onboarding-options single-option">
          <article className="featured">
            <div className="workspace-onboarding-option-icon">
              <FactCheckOutlined fontSize="small" />
            </div>
            <div className="workspace-onboarding-option-copy">
              <span>Quick setup request</span>
              <h3>One short form, then we take it forward.</h3>
              <p>
                No long questionnaire. We only collect the basic contact and company
                details needed to start the Workspace setup conversation.
              </p>
              <ul>
                <li><CheckCircle fontSize="small" />Name, mobile number and company name only</li>
                <li><CheckCircle fontSize="small" />Verification and setup guidance handled by our team</li>
              </ul>
            </div>
            {onboardingPrompt ? (
              <button onClick={() => chooseOnboarding("QUICK_SETUP")} type="button">
                Continue setup
                <ArrowForward fontSize="small" />
              </button>
            ) : (
              <a href={signUpUrl}>
                Start setup
                <ArrowForward fontSize="small" />
              </a>
            )}
          </article>
        </div>
      </section>

      <section className="workspace-landing-section">
        <div className="workspace-landing-section-heading">
          <span>Built around real operations</span>
          <h2>Everything important remains connected to the work.</h2>
          <p>
            Each module works independently while contributing to one company-wide view.
          </p>
        </div>
        <div className="workspace-capability-grid">
          {capabilities.map(({ icon: Icon, title, text }, index) => (
            <article key={title}>
              <div className="workspace-capability-number">0{index + 1}</div>
              <i><Icon fontSize="small" /></i>
              <h3>{title}</h3>
              <p>{text}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="workspace-workflow-section">
        <div className="workspace-workflow-copy">
          <span>One continuous workflow</span>
          <h2>Operational clarity without adding operational noise.</h2>
          <p>
            Workspace is designed around a simple rhythm: capture the right context,
            coordinate ownership, deliver visibly and improve from evidence.
          </p>
          <a href={signInUrl}>
            Enter Workspace
            <ArrowForward fontSize="small" />
          </a>
        </div>
        <div className="workspace-workflow-steps">
          {workflowSteps.map(([number, title, text]) => (
            <article key={number}>
              <span>{number}</span>
              <div>
                <h3>{title}</h3>
                <p>{text}</p>
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="workspace-enquiry-section" id="workspace-enquiry">
        <div className="workspace-enquiry-copy">
          <span>Talk to the Workspace team</span>
          <h2>See how your company would run inside one connected workspace.</h2>
          <p>
            Share the basics and we will map the most useful modules for your company.
          </p>

          <div className="workspace-enquiry-benefits">
            <article>
              <CheckCircle fontSize="small" />
              <div>
                <strong>Workflow-specific walkthrough</strong>
                <span>Focused on your delivery, CRM, people and finance process.</span>
              </div>
            </article>
            <article>
              <CheckCircle fontSize="small" />
              <div>
                <strong>Practical onboarding plan</strong>
                <span>Roles, modules, data migration and rollout priorities.</span>
              </div>
            </article>
            <article>
              <CheckCircle fontSize="small" />
              <div>
                <strong>Launch offer eligibility</strong>
                <span>First 2 projects and 10 employees free for lifetime, then pay only for active employees.</span>
              </div>
            </article>
          </div>

          <button onClick={onOpenDocumentation} type="button">
            <Description fontSize="small" />
            Review product documentation
          </button>
        </div>

        <form className="workspace-enquiry-form" onSubmit={handleEnquirySubmit}>
          <div className="workspace-enquiry-form-heading">
            <div>
              <span>Workspace enquiry</span>
              <h3>Request a free walkthrough</h3>
            </div>
            <SupportAgent fontSize="small" />
          </div>

          <div className="workspace-enquiry-fields">
            <label>
              <span>Your name</span>
              <input
                autoComplete="name"
                maxLength={120}
                name="name"
                onChange={updateEnquiry}
                placeholder="Full name"
                required
                value={enquiry.name}
              />
            </label>
            <label>
              <span>Mobile number</span>
              <input
                autoComplete="tel"
                maxLength={40}
                name="phone"
                onChange={updateEnquiry}
                placeholder="+91 98765 43210"
                required
                type="tel"
                value={enquiry.phone}
              />
            </label>
            <label>
              <span>Company name</span>
              <input
                autoComplete="organization"
                maxLength={160}
                name="company"
                onChange={updateEnquiry}
                placeholder="Company name"
                required
                value={enquiry.company}
              />
            </label>
            <label className="workspace-enquiry-honeypot" aria-hidden="true">
              <span>Website</span>
              <input
                autoComplete="off"
                name="website"
                onChange={updateEnquiry}
                tabIndex={-1}
                value={enquiry.website}
              />
            </label>
          </div>

          {enquiryState.error ? (
            <p className="workspace-enquiry-feedback error" role="alert">
              {enquiryState.error}
            </p>
          ) : null}
          {enquiryState.success ? (
            <p className="workspace-enquiry-feedback success" role="status">
              <CheckCircle fontSize="small" />
              {enquiryState.success}
            </p>
          ) : null}

          <button
            className="workspace-enquiry-submit"
            disabled={enquiryState.submitting}
            type="submit"
          >
            {enquiryState.submitting ? "Requesting walkthrough..." : "Request free walkthrough"}
            {!enquiryState.submitting ? <Send fontSize="small" /> : null}
          </button>
          <small className="workspace-enquiry-consent">
            By submitting, you agree that Lyriclious may contact you about Workspace.
            Your details are sent securely to the Lyriclious Leads team.
          </small>
        </form>
      </section>

      <section className="workspace-landing-cta">
        <div className="workspace-landing-cta-copy">
          <span><LockOutlined fontSize="small" />Ready when your team is</span>
          <h2>Give every project one clear path from plan to delivery.</h2>
          <p>
            Organize work your way, then move seamlessly between list, board and timeline
            views without losing context, ownership or momentum.
          </p>
          <div className="workspace-landing-cta-actions">
            <a href={signUpUrl}>
              Start for free
              <ArrowForward fontSize="small" />
            </a>
            <small><strong>Free forever.</strong><span>2 projects and 10 employees included.</span></small>
          </div>
        </div>

        <div className="workspace-landing-cta-product" aria-label="Workspace project views preview">
          <div aria-label="Project preview views" className="workspace-cta-product-tabs" role="tablist">
            {[["List", ViewListOutlined], ["Board", ViewKanbanOutlined], ["Gantt", TimelineOutlined]].map(([view, Icon]) => (
              <button aria-selected={activeProjectView === view} className={activeProjectView === view ? "active" : ""} key={view} onClick={() => setActiveProjectView(view)} role="tab" type="button">
                <Icon fontSize="small" />{view}
              </button>
            ))}
          </div>
          <div className="workspace-cta-product-body">
            <section className={`workspace-cta-list-preview${activeProjectView === "List" ? " is-active" : ""}`} aria-label="List view preview">
              <div className="workspace-cta-preview-heading"><span>Website launch</span><small>12 work items</small></div>
              <div className="workspace-cta-list-columns"><span>Name</span><span>Owner</span><span>Due</span></div>
              <div className="workspace-cta-list-row"><i className="done" /><span><strong>Design system</strong><small>Milestone · 2 subtasks</small></span><em><b>NS</b><b>AK</b></em><time>Done</time></div>
              <div className="workspace-cta-list-row"><i className="progress" /><span><strong>Homepage refresh</strong><small>High priority · 5 subtasks</small></span><em><b>AK</b><b>RS</b></em><time className="today">Today</time></div>
              <div className="workspace-cta-list-row"><i className="review" /><span><strong>Client approval</strong><small>Waiting on approval</small></span><em><b>SK</b></em><time>Aug 14</time></div>
              <div className="workspace-cta-list-row"><i /><span><strong>QA checklist</strong><small>Required before release</small></span><em><b>NS</b><b>VP</b></em><time>Aug 16</time></div>
            </section>

            <section className={`workspace-cta-board-preview${activeProjectView === "Board" ? " is-active" : ""}`} aria-label="Board view preview">
              <div className="workspace-cta-board-column">
                <div><span>Ready</span><small>03</small></div>
                <article><strong>QA checklist</strong><span><i className="priority-medium" />3 subtasks</span><em><b>VP</b></em></article>
                <article><strong>Production release</strong><span><i />Aug 16</span><em><b>RS</b><b>AK</b></em></article>
              </div>
              <div className="workspace-cta-board-column active">
                <div><span>In progress</span><small>02</small></div>
                <article><strong>Homepage refresh</strong><span><i />Due today</span><em><b>AK</b><b>NS</b></em></article>
                <article><strong>Design system</strong><span><i className="priority-low" />6 comments</span><em><b>AK</b></em></article>
              </div>
              <div className="workspace-cta-board-column review">
                <div><span>In review</span><small>02</small></div>
                <article><strong>Client approval</strong><span><i className="priority-review" />Client review</span><em><b>SK</b></em></article>
                <article><strong>Content review</strong><span><i className="priority-medium" />Awaiting sign-off</span><em><b>NS</b></em></article>
              </div>
              <p className="workspace-cta-board-drag-hint">Drag &amp; drop to update status</p>
            </section>

            <section className={`workspace-cta-timeline-preview${activeProjectView === "Gantt" ? " is-active" : ""}`} aria-label="Gantt view preview">
              <div className="workspace-cta-preview-heading"><span>Website launch</span><small>Critical path</small></div>
              <div className="workspace-cta-timeline-days"><span>12</span><span>13</span><span>14</span><span>15</span><span>16</span></div>
              <div className="workspace-cta-timeline-row"><small>Design</small><div className="workspace-cta-timeline-bar purple">Design system <em><b>NS</b><b>AK</b></em></div></div>
              <div className="workspace-cta-timeline-row"><small>Build</small><div className="workspace-cta-timeline-bar blue">Homepage refresh + QA <em><b>AK</b><b>RS</b><b>VP</b></em></div></div>
              <div className="workspace-cta-timeline-row"><small>Review</small><div className="workspace-cta-timeline-bar pink">Client approval <em><b>SK</b></em></div></div>
              <div className="workspace-cta-timeline-milestone">Launch readiness</div>
            </section>
          </div>
        </div>
      </section>
    </main>
  );
}
